import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Payment from "@mui/icons-material/Payment";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { defaultVariables } from "../../../config";
import { currencySymbol, price } from "../../../config/values";
import AppAnimate from "../../components/AppAnimate";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../config/design/styles";
import SavedCardsSection from "./components/SavedCardsSection";
import { paymentMethodAddErrorMessage, validateCardForStore } from "./cardValidation";

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("ACT")}`,
});

const WalletParamPayPage = ({ classes }) => {
    const { transactionId, orderId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [transaction, setTransaction] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [payLoading, setPayLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [defaultUpdatingId, setDefaultUpdatingId] = useState(null);
    const [form, setForm] = useState({
        holder_name: "",
        card_number: "",
        exp_month: "",
        exp_year: "",
        label: "",
    });

    const applyCardsAndSelection = useCallback((list) => {
        const safe = Array.isArray(list) ? list : [];
        setCards(safe);
        setSelectedId((sel) => {
            if (safe.length === 0) {
                return null;
            }
            if (sel != null && safe.some((c) => c.id === sel)) {
                return sel;
            }
            const def = safe.find((c) => c.is_default);
            if (def) {
                return def.id;
            }
            return safe[0].id;
        });
    }, []);

    const loadInitial = useCallback(async () => {
        setLoading(true);
        try {
            const [txRes, cardsRes] = await Promise.all([
                axios.get(`${defaultVariables.url}/wallets/pending-transaction/${transactionId}`, {
                    headers: authHeaders(),
                }),
                axios.get(`${defaultVariables.url}/payment-methods`, { headers: authHeaders() }),
            ]);
            setTransaction(txRes.data);
            applyCardsAndSelection(cardsRes.data || []);
        } catch (e) {
            enqueueSnackbar(t("paramPayLoadError"), { variant: "error" });
            navigate("/account/wallet");
        } finally {
            setLoading(false);
        }
    }, [transactionId, navigate, enqueueSnackbar, t, applyCardsAndSelection]);

    const refreshCardsOnly = useCallback(async () => {
        try {
            const { data } = await axios.get(`${defaultVariables.url}/payment-methods`, { headers: authHeaders() });
            applyCardsAndSelection(data || []);
        } catch (e) {
            enqueueSnackbar(t("paramPayLoadError"), { variant: "error" });
        }
    }, [applyCardsAndSelection, enqueueSnackbar, t]);

    const setCardDefault = async (id, isDefault) => {
        setDefaultUpdatingId(id);
        try {
            await axios.patch(
                `${defaultVariables.url}/payment-methods/${id}`,
                { is_default: isDefault },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } },
            );
            setCards((prev) =>
                isDefault
                    ? prev.map((c) => ({ ...c, is_default: c.id === id }))
                    : prev.map((c) => (c.id === id ? { ...c, is_default: false } : c)),
            );
            if (isDefault) {
                setSelectedId(id);
            }
        } catch (e) {
            enqueueSnackbar(t("paramPayCardDefaultFailed"), { variant: "error" });
        } finally {
            setDefaultUpdatingId(null);
        }
    };

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const payWithStored = async () => {
        if (!selectedId) {
            enqueueSnackbar(t("paramPaySelectCard"), { variant: "warning" });
            return;
        }
        setPayLoading(true);
        try {
            const body = {
                wallet_history_id: Number(transactionId),
                payment_method_id: selectedId,
                platform: "web",
            };
            if (orderId) {
                body.order_id = Number(orderId);
            }
            const res = await axios.post(`${defaultVariables.url}/payment/param/stored-start`, body, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });
            if (res.data.completed && res.data.redirect_url) {
                window.location.href = res.data.redirect_url;
            } else if (res.data.redirect_url) {
                window.location.href = res.data.redirect_url;
            }
        } catch (e) {
            const msg = e.response?.data?.message || t("paramPayFailed");
            enqueueSnackbar(msg, { variant: "error" });
        } finally {
            setPayLoading(false);
        }
    };

    const removeCard = async (id) => {
        try {
            await axios.delete(`${defaultVariables.url}/payment-methods/${id}`, { headers: authHeaders() });
            enqueueSnackbar(t("paramPayCardRemoved"), { variant: "success" });
            setCards((prev) => {
                const next = prev.filter((c) => c.id !== id);
                setSelectedId((sel) => {
                    if (sel !== id) {
                        return sel;
                    }
                    if (!next.length) {
                        return null;
                    }
                    const def = next.find((c) => c.is_default);
                    return def ? def.id : next[0].id;
                });
                return next;
            });
        } catch (e) {
            enqueueSnackbar(t("paramPayCardRemoveFailed"), { variant: "error" });
        }
    };

    const addCard = async (e) => {
        e.preventDefault();
        const localErr = validateCardForStore(form);
        if (localErr) {
            enqueueSnackbar(t(`paramPayCardError_${localErr}`), { variant: "error" });
            return;
        }
        setAddLoading(true);
        try {
            const res = await axios.post(`${defaultVariables.url}/payment-methods`, form, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });
            const newCard = res.data;
            enqueueSnackbar(t("paramPayCardAdded"), { variant: "success" });
            setAddOpen(false);
            setForm({
                holder_name: "",
                card_number: "",
                exp_month: "",
                exp_year: "",
                label: "",
            });
            if (newCard && newCard.id != null) {
                setCards((prev) => [newCard, ...prev.filter((c) => c.id !== newCard.id)]);
                setSelectedId(newCard.id);
            } else {
                refreshCardsOnly();
            }
        } catch (e) {
            enqueueSnackbar(paymentMethodAddErrorMessage(e, t), { variant: "error" });
        } finally {
            setAddLoading(false);
        }
    };

    if (loading) {
        return (
            <AppAnimate animation="transition.slideUpIn" delay={200} title={t("paramPayTitle")}>
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            </AppAnimate>
        );
    }

    return (
        <AppAnimate animation="transition.slideUpIn" delay={200} title={t("paramPayTitle")}>
            <Box className="page-color-layout" maxWidth={480} mx="auto" py={3} px={2}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <IconButton size="small" onClick={() => navigate(-1)} aria-label="back" className={classes.dialogClose}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6">{t("paramPayTitle")}</Typography>
                </Box>

                {transaction && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography color="text.secondary" variant="body2">
                                {t("amount")}
                            </Typography>
                            <Typography variant="h5">
                                {price(transaction.amount)} {currencySymbol}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                <SavedCardsSection
                    selectable
                    selectedId={selectedId}
                    onSelectCard={setSelectedId}
                    cards={cards}
                    addOpen={addOpen}
                    setAddOpen={setAddOpen}
                    form={form}
                    setForm={setForm}
                    addLoading={addLoading}
                    onAddSubmit={addCard}
                    onRemoveCard={removeCard}
                    onDefaultChange={setCardDefault}
                    defaultUpdatingId={defaultUpdatingId}
                />

                {cards.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<Payment />}
                            disabled={payLoading || !selectedId}
                            onClick={payWithStored}
                            sx={{ mb: 2 }}
                        >
                            {payLoading ? <CircularProgress size={22} color="inherit" /> : t("paramPayWithCard")}
                        </Button>
                    </>
                )}
            </Box>
        </AppAnimate>
    );
};

export default withStyles(Styles)(WalletParamPayPage);
